import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate, fmtMoney } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "./StatusBadge";
import PlacementDialog from "./PlacementDialog";
import TermDialog from "./TermDialog";
import { Plus, RefreshCw, Pencil, History, Ban, RotateCcw, BadgeCheck } from "lucide-react";

function Terms({ pid }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/placements/${pid}/terms`).then((r) => setRows(r.data.items)); }, [pid]);
  if (!rows) return <p className="text-xs text-slate-400 py-2">Loading history…</p>;
  if (!rows.length) return <p className="text-xs text-slate-400 py-2">No contract terms yet.</p>;
  return (
    <ul className="mt-2 divide-y divide-slate-100 rounded-lg border border-slate-100" data-testid={`terms-${pid}`}>
      {rows.map((c) => (
        <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-xs">
          <span className="font-medium text-slate-700 tnum">{fmtDate(c.start_date)} – {fmtDate(c.end_date)} · {c.term}{c.is_renewal ? " · Renewal" : ""}</span>
          <span className="flex items-center gap-2 text-slate-500">
            <span className="tnum">{fmtMoney(c.monthly_rate)}/mo</span>
            <span className={`rounded px-1.5 py-0.5 font-semibold ${c.term_state === "Current" ? "bg-[#00C9E0]/15 text-[#0094A8]" : "bg-slate-100 text-slate-500"}`}>{c.term_state}</span>
            <button className="text-[#0094A8] hover:underline" onClick={() => navigate(`/contracts/${c.id}`)} data-testid={`term-open-${c.id}`}>{c.contract_number}</button>
          </span>
        </li>
      ))}
    </ul>
  );
}

export default function PlacementsPanel({ businessId, onChanged }) {
  const { user } = useAuth();
  const isManager = ["tenant_owner", "platform_owner", "sales_manager"].includes(user?.role);
  const canWrite = user?.role !== "readonly";
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(undefined);
  const [term, setTerm] = useState(null);
  const [open, setOpen] = useState({});
  const load = useCallback(() => api.get(`/placements?business_id=${businessId}`).then((r) => setRows(r.data.items)), [businessId]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChanged?.(); };
  const status = async (p, action) => {
    const reason = action === "reactivate" ? "" : window.prompt(`Reason for ${action === "cancel" ? "cancelling" : "marking former"} (kept in history):`) ?? null;
    if (reason === null) return;
    try { await api.post(`/placements/${p.id}/status`, { action, reason }); toast.success("Status updated — history kept"); refresh(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="placements-panel">
      <div className="flex items-center justify-between p-4 border-b border-slate-100">
        <div><h3 className="font-display font-semibold text-slate-800">Advertising Placements</h3>
          <p className="text-xs text-slate-400">{rows.length} zone{rows.length === 1 ? "" : "s"} · one master company record</p></div>
        {canWrite && <Button size="sm" onClick={() => setEdit(null)} data-testid="placement-add"><Plus className="h-4 w-4" />Add Zone</Button>}
      </div>
      {rows.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">No placements yet. Add the first zone this company advertises in.</p> : (
        <div className="divide-y divide-slate-100">
          {rows.map((p) => (
            <div key={p.id} className="p-4" data-testid={`placement-${p.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800">{p.territory} › {p.zone}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{p.ad_type} · {p.positions} position{p.positions === 1 ? "" : "s"}{p.print_position ? ` · Pos. ${p.print_position}` : ""} · {p.pricing_status || "Regular"} · {fmtMoney(p.monthly_price)}/mo · {p.assigned_to_name || "Unassigned"}</p>
                  <p className="text-xs text-slate-400 mt-0.5 tnum">{p.contract ? `${fmtDate(p.contract.start_date)} – ${fmtDate(p.contract.end_date)} (${p.contract.term})` : "No contract term"}{p.days_left != null && p.days_left >= 0 ? ` · ${p.days_left}d left` : ""}</p>
                  <p className="text-xs text-slate-400 mt-0.5">Website: {p.website_profile_status || "—"} · Coupon: {p.coupon_status || "—"} · {p.current_ad ? <span className="text-[#4A700A] font-semibold inline-flex items-center gap-0.5"><BadgeCheck className="h-3 w-3" />Approved ad on file</span> : "No approved ad"}{p.renewal_stage ? ` · Manual renewal stage: ${p.renewal_stage}` : ""}</p>
                </div>
                <StatusBadge status={p.contract_status} testid={`placement-status-${p.id}`} />
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setOpen({ ...open, [p.id]: !open[p.id] })} data-testid={`placement-history-${p.id}`}><History className="h-3 w-3" />Contract history</Button>
                {canWrite && !["Cancelled", "Former Advertiser"].includes(p.status) && <>
                  {p.contract && <Button size="sm" className="h-7 text-xs" onClick={() => setTerm({ p, renewal: true })} data-testid={`placement-renew-${p.id}`}><RefreshCw className="h-3 w-3" />Renew</Button>}
                  {!p.contract && <Button size="sm" className="h-7 text-xs" onClick={() => setTerm({ p, renewal: false })} data-testid={`placement-term-${p.id}`}><Plus className="h-3 w-3" />Add term</Button>}
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEdit(p)} data-testid={`placement-edit-${p.id}`}><Pencil className="h-3 w-3" />Edit</Button>
                </>}
                {isManager && !["Cancelled", "Former Advertiser"].includes(p.status) && <>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => status(p, "cancel")} data-testid={`placement-cancel-${p.id}`}><Ban className="h-3 w-3" />Cancel</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => status(p, "former")} data-testid={`placement-former-${p.id}`}>Mark former</Button>
                </>}
                {isManager && ["Cancelled", "Former Advertiser"].includes(p.status) && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => status(p, "reactivate")} data-testid={`placement-reactivate-${p.id}`}><RotateCcw className="h-3 w-3" />Reactivate</Button>}
              </div>
              {open[p.id] && <Terms pid={p.id} key={`${p.id}-${p.current_contract_id}`} />}
            </div>
          ))}
        </div>
      )}
      <PlacementDialog businessId={businessId} placement={edit || null} open={edit !== undefined} onOpenChange={(o) => !o && setEdit(undefined)} onSaved={refresh} />
      <TermDialog placement={term?.p} renewal={term?.renewal} open={!!term} onOpenChange={(o) => !o && setTerm(null)} onSaved={refresh} />
    </div>
  );
}
